import { MAX_GUN_TIER, type WeaponCategory } from '@/config/weapons';
import { CARD_VALUES } from '@/config/cards';
import { ECONOMY } from '@/config/economy';
import { PLAYER } from '@/config/player';
import { t } from '@/i18n';
import type { PlayerStats } from './stats';
import type { WeaponHolder } from '@/entities/weapons';
import type { Player } from '@/entities/player';
import { SPECIAL_CARDS } from './cards-special';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';
export type Currency = 'yellow' | 'red';
/** Usage type — drives the card's tint so builds read at a glance (docs/03) */
export type CardKind = 'offense' | 'defense' | 'mobility' | 'economy' | 'special';

export interface CardContext {
  stats: PlayerStats;
  weapon: WeaponHolder;
  player: Player;
  /** How many copies of a card id the collection holds (stack level) */
  owned(id: string): number;
}

export interface CardDef {
  id: string;
  /** Icon glyph key — drawn procedurally in the shop (readable without text) */
  icon: string;
  /** Trade-off cards (docs/12 C2): stat icons shown as green-up / red-down */
  tradeoff?: { up: string; down: string };
  nameKey: string;
  descKey: string;
  kind: CardKind;
  rarity: Rarity;
  /** Red gems are the rare boss-kill currency for tier/epic recipes (docs/03) */
  currency: Currency;
  /** Relative offer probability inside its rarity band (default 1) */
  weight?: number;
  /** Stack cap — hidden from offers once the collection holds this many */
  maxStacks?: number;
  apply(ctx: CardContext): void;
  /** Returning false hides the card from offers in the current run state */
  canOffer?(ctx: CardContext): boolean;
}

/** Per-category DAMAGE card (docs/12 M5: one stat per card). */
function catCard(cat: WeaponCategory): CardDef {
  return {
    id: `cat-${cat}`, icon: `dmg-${cat}`, kind: 'offense', rarity: 'common',
    currency: 'yellow', weight: 0.8,
    nameKey: `card.cat${cat}.name`, descKey: `card.cat${cat}.desc`,
    apply: ({ stats }) => { stats.catDamage[cat] *= CARD_VALUES.catDamage; },
  };
}

/** Per-category FIRE RATE card — its own family, mix and match (M5). */
function rateCard(cat: WeaponCategory): CardDef {
  return {
    id: `rate-${cat}`, icon: `rate-${cat}`, kind: 'offense', rarity: 'common',
    currency: 'yellow', weight: 0.8,
    nameKey: `card.rate${cat}.name`, descKey: `card.rate${cat}.desc`,
    apply: ({ stats }) => { stats.catFireRate[cat] *= CARD_VALUES.catFireRate; },
  };
}

/**
 * Card pool — docs/03-upgrades-economy.md.
 * Kind = tint, rarity = frame/badge; stacks show as LV in the shop.
 */
export const CARDS: ReadonlyArray<CardDef> = [
  // ---- Common (yellow): stackable build bricks
  {
    id: 'damage', icon: 'damage', kind: 'offense', rarity: 'common', currency: 'yellow',
    nameKey: 'card.damage.name', descKey: 'card.damage.desc',
    apply: ({ stats }) => { stats.damageMul *= CARD_VALUES.damage; },
  },
  catCard('bullet'),
  catCard('plasma'),
  catCard('laser'),
  catCard('bomb'),
  rateCard('bullet'),
  rateCard('plasma'),
  rateCard('laser'),
  rateCard('bomb'),
  {
    id: 'projspeed', icon: 'projspeed', kind: 'offense', rarity: 'common', currency: 'yellow',
    nameKey: 'card.projspeed.name', descKey: 'card.projspeed.desc', maxStacks: 5,
    apply: ({ stats }) => { stats.projSpeedMul *= CARD_VALUES.projSpeed; },
  },
  {
    id: 'range', icon: 'range', kind: 'offense', rarity: 'common', currency: 'yellow',
    nameKey: 'card.range.name', descKey: 'card.range.desc', maxStacks: 5,
    apply: ({ stats }) => { stats.rangeMul *= CARD_VALUES.range; },
  },
  {
    id: 'critchance', icon: 'critchance', kind: 'offense', rarity: 'uncommon', currency: 'yellow',
    nameKey: 'card.critchance.name', descKey: 'card.critchance.desc', maxStacks: 5,
    apply: ({ stats }) => { stats.critChanceBonus += CARD_VALUES.critChance; },
  },
  {
    id: 'critdamage', icon: 'critdamage', kind: 'offense', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.critdamage.name', descKey: 'card.critdamage.desc', maxStacks: 5,
    apply: ({ stats }) => { stats.critDamageBonus += CARD_VALUES.critDamage; },
  },
  {
    id: 'speed', icon: 'speed', kind: 'mobility', rarity: 'common', currency: 'yellow',
    nameKey: 'card.speed.name', descKey: 'card.speed.desc',
    apply: ({ stats }) => { stats.speedMul *= CARD_VALUES.speed; },
  },
  {
    id: 'dashcd', icon: 'dashcd', kind: 'mobility', rarity: 'uncommon', currency: 'yellow',
    nameKey: 'card.dashcd.name', descKey: 'card.dashcd.desc', maxStacks: 4,
    apply: ({ stats }) => { stats.dashCooldownMul *= CARD_VALUES.dashCooldown; },
  },
  {
    id: 'magnet', icon: 'magnet', kind: 'economy', rarity: 'common', currency: 'yellow',
    nameKey: 'card.magnet.name', descKey: 'card.magnet.desc',
    apply: ({ stats }) => { stats.magnetMul *= CARD_VALUES.magnet; },
  },
  {
    id: 'greed', icon: 'greed', kind: 'economy', rarity: 'common', currency: 'yellow',
    nameKey: 'card.greed.name', descKey: 'card.greed.desc', maxStacks: 4,
    apply: ({ stats }) => { stats.greedMul *= CARD_VALUES.greed; },
  },
  {
    id: 'energy', icon: 'energy', kind: 'special', rarity: 'uncommon', currency: 'yellow',
    nameKey: 'card.energy.name', descKey: 'card.energy.desc', maxStacks: 3,
    apply: ({ stats }) => { stats.energyBonus += 1; },
  },

  // ---- Rare (yellow)
  {
    // The strategic "boost everything" option: rarer AND lower-weighted than
    // the category cards on purpose (docs/03 — pick-your-lane decision)
    id: 'firerate', icon: 'firerate', kind: 'offense', rarity: 'uncommon', currency: 'yellow',
    weight: 0.5,
    nameKey: 'card.firerate.name', descKey: 'card.firerate.desc',
    apply: ({ stats }) => { stats.fireRateMul *= CARD_VALUES.fireRate; },
  },
  {
    id: 'supershot', icon: 'supershot', kind: 'offense', rarity: 'uncommon', currency: 'yellow',
    nameKey: 'card.supershot.name', descKey: 'card.supershot.desc', maxStacks: 4,
    apply: ({ stats }) => { stats.supershotMul *= CARD_VALUES.supershot; },
  },
  {
    id: 'focus', icon: 'superfan', kind: 'offense', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.focus.name', descKey: 'card.focus.desc', maxStacks: 3,
    apply: ({ stats }) => { stats.superFanBonus += 1; },
  },
  {
    id: 'heart', icon: 'heart', kind: 'defense', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.heart.name', descKey: 'card.heart.desc',
    canOffer: ({ stats }) => stats.bonusHp < PLAYER.maxBonusCells,
    apply: ({ stats, player }) => {
      stats.bonusHp += 1;
      player.hp += PLAYER.cellHp;
    },
  },
  {
    id: 'shieldstart', icon: 'shieldstart', kind: 'defense', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.shieldstart.name', descKey: 'card.shieldstart.desc', maxStacks: 2,
    apply: ({ stats }) => { stats.shieldStart += 1; },
  },
  {
    id: 'iframes', icon: 'iframes', kind: 'defense', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.iframes.name', descKey: 'card.iframes.desc', maxStacks: 3,
    apply: ({ stats }) => { stats.iframeMul *= CARD_VALUES.iframes; },
  },
  {
    id: 'regen', icon: 'regen', kind: 'defense', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.regen.name', descKey: 'card.regen.desc', maxStacks: 2,
    apply: ({ stats }) => { stats.regenCells += 1; },
  },
  // Defense expansion (docs/12 C5)
  {
    id: 'bulwark', icon: 'bulwark', kind: 'defense', rarity: 'uncommon', currency: 'yellow',
    nameKey: 'card.bulwark.name', descKey: 'card.bulwark.desc', maxStacks: 5,
    apply: ({ stats }) => { stats.damageTakenMul *= CARD_VALUES.bulwark; },
  },
  {
    id: 'salvager', icon: 'salvager', kind: 'defense', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.salvager.name', descKey: 'card.salvager.desc', maxStacks: 2,
    apply: ({ stats }) => { stats.armorOnKill += 1; },
  },
  {
    // Defense that SCALES with offense — the more damage cards you stack,
    // the harder the hull gets (docs/12 C5)
    id: 'aegislink', icon: 'aegis', kind: 'defense', rarity: 'epic', currency: 'red',
    nameKey: 'card.aegislink.name', descKey: 'card.aegislink.desc', maxStacks: 1,
    apply: ({ stats }) => { stats.aegisLink = true; },
  },
  {
    id: 'discount', icon: 'discount', kind: 'economy', rarity: 'uncommon', currency: 'yellow',
    nameKey: 'card.discount.name', descKey: 'card.discount.desc', maxStacks: 3,
    apply: ({ stats }) => { stats.discountMul *= CARD_VALUES.discount; },
  },
  {
    id: 'luckychip', icon: 'luckychip', kind: 'economy', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.luckychip.name', descKey: 'card.luckychip.desc', maxStacks: 1,
    apply: ({ stats }) => { stats.rerollLock = true; },
  },
  {
    // Tiered reroll price cut: -10% / -25% / -50% by stack (docs/12 I6)
    id: 'rerolldiscount', icon: 'rerolldiscount', kind: 'economy', rarity: 'uncommon',
    currency: 'yellow', maxStacks: 3,
    nameKey: 'card.rerolldiscount.name', descKey: 'card.rerolldiscount.desc',
    apply: ({ stats }) => { stats.rerollDiscountSteps += 1; },
  },
  {
    // Plating Deal: yellow armor-buy gets cheaper (docs/12 O)
    id: 'platingdeal', icon: 'shieldstart', kind: 'economy', rarity: 'uncommon',
    currency: 'yellow', maxStacks: 3,
    nameKey: 'card.platingdeal.name', descKey: 'card.platingdeal.desc',
    apply: ({ stats }) => { stats.armorPriceMul *= 0.75; },
  },
  {
    id: 'cargo', icon: 'cargo', kind: 'economy', rarity: 'rare', currency: 'yellow',
    nameKey: 'card.cargo.name', descKey: 'card.cargo.desc',
    maxStacks: ECONOMY.inventory.total - ECONOMY.inventory.start,
    apply: ({ stats }) => { stats.cargoSlots += 1; },
  },
  {
    id: 'barrel', icon: 'barrel', kind: 'special', rarity: 'epic', currency: 'red',
    nameKey: 'card.barrel.name', descKey: 'card.barrel.desc',
    canOffer: ({ weapon }) => weapon.gunTier < MAX_GUN_TIER,
    // Clamped: owned cards are re-applied after a death restart (docs/03)
    apply: ({ weapon }) => { weapon.gunTier = Math.min(MAX_GUN_TIER, weapon.gunTier + 1); },
  },
  ...SPECIAL_CARDS,
];

export function cardName(card: CardDef): string {
  return t(card.nameKey as Parameters<typeof t>[0]);
}

export function cardDescription(card: CardDef): string {
  return t(card.descKey as Parameters<typeof t>[0]);
}
